import { useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useRegisterOverlay } from '@/stores/overlayStore';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /**
   * Accessible name for the dialog when no visible `title` is rendered
   * (e.g. `bare` confirmation modals). Falls back to `title` when set.
   * Use this so screen readers announce a name without forcing the
   * bordered header + close button that `title` brings.
   */
  ariaLabel?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Remove default body padding — caller controls all content layout */
  bare?: boolean;
  /**
   * Size variant — controls the inner card's max width and height.
   * - `md` (default): max-w-lg, max-h-[85dvh], centered
   * - `lg`: max-w-2xl, near-full viewport pinned to the bottom with rounded top — used for page-like modals (review, edit)
   */
  size?: 'md' | 'lg';
  /**
   * Whether a backdrop tap dismisses the modal (calls `onClose`). Defaults
   * to `true`. Set `false` for confirmations that require an explicit CTA
   * choice (e.g. the walkthrough "Leave without saving?" — YAR-238 AC 10).
   * Escape is unaffected.
   */
  dismissOnBackdrop?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  bare = false,
  size = 'md',
  dismissOnBackdrop = true,
}: ModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  // Pauses (and hides) the bottom toast while this covers it (YAR-476).
  useRegisterOverlay(open);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return createPortal(
    <div
      className={[
        'fixed inset-0 z-[200] flex justify-center',
        size === 'lg' ? 'items-end' : 'items-center px-5',
      ].join(' ')}
      onClick={dismissOnBackdrop ? onClose : undefined}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[5px]" />

      {/* Content */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title ?? ariaLabel}
        onClick={(e) => e.stopPropagation()}
        className={[
          'relative bg-card text-card-foreground shadow-[0px_0px_16px_-4px_rgba(56,64,74,0.1)] border border-border w-full flex flex-col',
          size === 'lg'
            ? 'max-w-2xl h-[calc(100dvh-24px)] rounded-t-xl border-b-0'
            : 'max-w-lg max-h-[85dvh] rounded-xl',
        ].join(' ')}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150 cursor-pointer"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Body */}
        {bare ? (
          children
        ) : (
          <div
            className={[
              'px-6 pt-4 overflow-y-auto flex-1',
              // A bottom-pinned (lg) modal with no footer ends flush at the
              // screen edge; reserve the iOS home-indicator inset so the last
              // content isn't occluded. env() is 0 elsewhere → stays 16px.
              size === 'lg' && !footer
                ? 'pb-[max(1rem,env(safe-area-inset-bottom))]'
                : 'pb-4',
            ].join(' ')}
          >
            {children}
          </div>
        )}

        {/* Footer */}
        {footer && (
          <div
            className={[
              'flex items-center justify-end gap-2 px-6 pt-4 border-t border-border',
              // On a bottom-pinned (lg) modal the footer is the bottom-most
              // element — reserve the home-indicator inset.
              size === 'lg'
                ? 'pb-[max(1rem,env(safe-area-inset-bottom))]'
                : 'pb-4',
            ].join(' ')}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
